'use client';

import React from 'react';
import Link from 'next/link';


import { useLogging } from 'src/context/logging';
import { getProfileUrl } from 'src/utils/profileUrl';
import type { FreshFacesLocation } from 'src/api/redux/profiles/profilesApi';

interface FreshFacesLocationRowProps {
  location: FreshFacesLocation;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr + 'T00:00:00Z');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const FreshFacesLocationRow: React.FC<FreshFacesLocationRowProps> = ({
  location,
}) => {
  const log = useLogging();
  const name = location.name || 'Unknown';

  return (
    <div
      className="flex items-center justify-between gap-2 py-3 border-b border-neutral-100 last:border-b-0"
      role="listitem"
      data-testid="fresh-faces-location-row"
    >
      <Link
        href={getProfileUrl(location.type, location.provider_id)}
        scroll={false}
        className="flex items-center gap-2 min-w-0 text-sky-900 no-underline"
        aria-label={`View profile for ${name}`}
        data-testid="fresh-faces-location-link"
        onClick={(e) => {
          e.stopPropagation();
          log.event('Location Clicked', {
            source: 'web',
            location: 'FreshFacesLocations',
            provider_id: location.provider_id,
          });
        }}
      >
        <span className="text-sm font-bold text-link truncate">
          {name}
        </span>
      </Link>
      <div className="flex items-center gap-4 flex-shrink-0">
        <span
          className="text-sm font-semibold text-teal-900 tabular-nums min-w-14 text-end"
          aria-label={`${location.claims.toLocaleString()} claims`}
          data-testid="fresh-faces-location-claims"
        >
          {location.claims.toLocaleString()}
        </span>
        <span
          className="text-xs text-secondary min-w-20 text-end"
          aria-label={`First billed ${formatDate(location.first_billed_at)}`}
          data-testid="fresh-faces-location-date"
        >
          {formatDate(location.first_billed_at)}
        </span>
      </div>
    </div>
  );
};

export default FreshFacesLocationRow;
